/* eslint-disable indent */
/* eslint-disable react/no-array-index-key */
import LoadingIcon from '@/components/LoadingIcon';
import {
  CaretDownOutlined,
  CaretUpOutlined,
  MenuUnfoldOutlined,
  ReadOutlined,
  SyncOutlined,
} from '@ant-design/icons';

import {
  useMedicalInsightHistory,
  useMedicalInsightSession,
  useNCCNGuideline,
  useTreatmentInsightQA,
} from '@/pages/combinedWard/hook/useTreatmentPlan';
import QAResultItem from '@/pages/combinedWard/treatmentPlan/components/QAResultItem';
import { Button, Empty, Input, Select, Spin, message } from '@sinohealth/butterfly-ui-components';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import MdEditor, { Plugins } from 'react-markdown-editor-lite';
import styles from './index.less';

const emptyMessageIcon = require('@/assets/images/common/empty_message.png');

MdEditor.unuse(Plugins.ListOrdered);
MdEditor.unuse(Plugins.ListUnordered);

/**
 * 智能问答组件属性接口
 * @interface IntelligentQAProps
 */
interface IntelligentQAProps {
  patientId?: string;
  planType?: 'treatment' | 'qa' | null;
  style?: React.CSSProperties | undefined;
  onClose?: () => void;
}

/**
 * 基于NCCN指南的病例智能问答组件
 * 提供病例相关问题的智能问答功能，包括推荐问题、病例诊断概要、CRS评分和自定义问题回答
 *
 * @component IntelligentQA
 * @param {IntelligentQAProps} props - 组件属性
 * @returns {React.ReactElement} 渲染的组件
 *
 * @example
 * // 基本用法
 * <IntelligentQA />
 */
const IntelligentQA: React.FC<IntelligentQAProps> = ({ patientId, planType, style, onClose }) => {
  // 当前输入的问题
  const [inputQuestion, setInputQuestion] = useState('');
  // 问答区域的引用
  const qaResultRef = useRef<HTMLDivElement>(null);
  // 控制内容区域是否展开
  const [isContentExpanded, setIsContentExpanded] = useState(true);

  const {
    guidelineData,
    nccnConfirmVisible,
    analyzing,
    selectedNccnType,
    isInitialized,
    setIsInitialized,
    setSelectedNccnType,
    setNccnConfirmVisible,
    setGuidelineParams,
  } = useNCCNGuideline();
  const {
    sessionInsightId,
    setSessionInsightId,
    setSessionParams,
    canAsk,
    setCanAsk,
    loadingQuestion,
    questionData,
    setQuestionData,
    httpGetMedicalInsightQuestionReq,
  } = useMedicalInsightSession();
  const { historyData, setHistoryParams, loadingHistory, setReloadHistory } =
    useMedicalInsightHistory((res) => {
      setAnswerContent('');
      setThinkingContent('');
      // 没有历史记录
      if (!res && planType === 'qa' && patientId) {
        // 2. API调用获取NCCN指南列表
        setGuidelineParams(patientId);
        setSelectedNccnType(undefined); // 初始化为未选中状态
      } else if (res) {
        // 3. 有历史记录,且有问题
        if (res?.question) {
          setIsInitialized(true);
          setQuestionData(res?.question);
          setSelectedNccnType(res?.guidelineName);
          setSessionInsightId(res?.id);
          setCanAsk(true);
        } else {
          // 4. 没有问题
          setGuidelineParams(patientId);
          setSelectedNccnType(undefined); // 初始化为未选中状态
        }
      }
    });

  const {
    thinking,
    thinkingContent,
    answerContent,
    connectLoading,
    completedQa,
    isAnswering,
    qaParams,
    sessionId,
    cursor,
    isReconnecting,
    isCompleted,
    currentQuestion,
    setAnswerContent,
    setCompletedQa,
    setThinkingContent,
    setQaParams,
    reconnect,
    restoreAndReconnect,
    clearSession,
    tryRestoreFromCache,
  } = useTreatmentInsightQA(sessionInsightId);

  // 🔥 新增：当有 sessionInsightId 时，确保 canAsk 为 true
  useEffect(() => {
    if (sessionInsightId && !canAsk) {
      setCanAsk(true);
    }
  }, [sessionInsightId]);

  useEffect(() => {
    // 当前回答完毕，进行刷新历史记录
    if (completedQa) {
      setReloadHistory(true);
      setCompletedQa(false);
    }
  }, [completedQa]);

  // 组件挂载时检查localStorage中是否有未完成的会话并恢复
  useEffect(() => {
    if (sessionInsightId && historyData) {
      // 检查历史记录中是否有未完成的问答
      try {
        const key = `qa_session_${sessionInsightId}`;
        const saved = localStorage.getItem(key);

        if (saved) {
          const session = JSON.parse(saved);
          console.log('检查到本地会话', session);

          // 只处理未完成的会话
          if (!session.isCompleted && session.question) {
            // 检查该问题是否已经在历史记录中且有答案（说明后端已完成）
            const existsInHistory = historyData?.qaHistory?.some(
              (item: any) => item.question === session.question && item.answer,
            );

            if (existsInHistory) {
              // 已经在历史记录中了，清除localStorage缓存
              console.log('问答已完成并保存到历史记录，清除本地缓存');
              clearSession(sessionInsightId);
            }
            // 注意：不在这里恢复，因为 hook 内部已经自动恢复了
          }
        }
      } catch (err) {
        console.error('检查未完成会话失败', err);
      }
    }
  }, [sessionInsightId, historyData]);

  // 监听网络状态变化，自动重连
  useEffect(() => {
    const handleOnline = () => {
      if (isAnswering && !isCompleted && sessionId) {
        message.info('网络已恢复，正在重新连接...');
        reconnect();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [isAnswering, isCompleted, sessionId]);

  // 切换内容区域展开/收起状态
  const toggleContentExpand = () => {
    setIsContentExpanded(!isContentExpanded);
  };

  /**
   * 组件挂载时自动初始化
   */
  useEffect(() => {
    // 1. 先查询历史记录
    if (planType === 'qa' && patientId && !isInitialized) {
      setHistoryParams({ patientId });
    }
  }, [planType]);

  /**
   * 处理NCCN指南选择变更
   */
  const handleNccnChange = (value: number | undefined, option: any) => {
    setSelectedNccnType(value);
  };

  /**
   * 确认NCCN指南并加载问答数据
   */
  const confirmQaNccnGuide = () => {
    if (!selectedNccnType) {
      message.error('请选择NCCN指南');
      return;
    }

    setSessionParams({
      patientId,
      guidelineName: selectedNccnType,
    });
    setNccnConfirmVisible(false);
    // 根据选择的NCCN指南类型加载相应的推荐问题
  };

  /**
   * 处理推荐问题点击事件
   * 根据点击的问题ID生成对应的答案
   *
   * @param {Question} question - 被点击的问题对象
   */
  const handleQuestionClick = (question: string) => {
    if (isAnswering) return;
    setQaParams({
      insightId: sessionInsightId,
      question,
    });
  };

  /**
   * 处理自定义问题提交事件
   * 生成并添加问答记录到历史列表
   */
  const handleSubmitQuestion = () => {
    if (!inputQuestion.trim()) {
      message.error('请输入您要提问的问题');
      return;
    }
    setQaParams({
      insightId: sessionInsightId,
      question: inputQuestion,
    });
    setInputQuestion('');
  };

  // 获取输入框的 placeholder 文本
  const getInputPlaceholder = () => {
    if (!isAnswering) return '询问关于此病例的问题...';
    if (isReconnecting) return '正在恢复会话...';
    return '正在生成回答...';
  };

  // 获取按钮文本
  const getButtonText = () => {
    if (!isAnswering) return '提交';
    if (isReconnecting) return '恢复中';
    return '生成中';
  };

  // 在组件顶部新增状态
  const userScrollTimeout = useRef<NodeJS.Timeout>();
  const isInitialLoad = useRef(true);
  const lastScrollPosition = useRef(0);
  const autoScrollEnabled = useRef(true);

  // 核心滚动逻辑
  const smartScroll = () => {
    if (!qaResultRef.current) return;
    const container = qaResultRef.current;
    const { scrollHeight, clientHeight, scrollTop } = container;

    // 首次加载强制滚动
    if (isInitialLoad.current) {
      container.scrollTo({ top: scrollHeight, behavior: 'auto' });
      isInitialLoad.current = false;
      return;
    }

    // 满足以下条件自动滚动：
    // 1. 正在生成回答或连接中
    // 2. 自动滚动启用
    if ((isAnswering || connectLoading) && autoScrollEnabled.current) {
      container.scrollTo({
        top: scrollHeight,
        behavior: 'auto',
      });
    }
  };

  // 首次加载和数据更新处理
  useEffect(() => {
    if (historyData && historyData?.qaHistory?.length > 0 && qaResultRef.current) {
      isInitialLoad.current = true;
      autoScrollEnabled.current = true;
      // 使用requestAnimationFrame确保DOM更新后再滚动
      requestAnimationFrame(() => {
        smartScroll();
      });
    }
  }, [historyData]);

  // 内容变化时立即滚动
  useEffect(() => {
    if ((isAnswering || connectLoading) && autoScrollEnabled.current) {
      // 使用requestAnimationFrame确保DOM更新后再滚动
      requestAnimationFrame(() => {
        smartScroll();
      });
    }
  }, [answerContent, thinkingContent, isAnswering, connectLoading]);

  // 回答状态变化处理
  useEffect(() => {
    if (isAnswering || connectLoading) {
      requestAnimationFrame(() => {
        smartScroll();
      });
    }
  }, [isAnswering, connectLoading]);

  // 用户滚动交互处理
  useEffect(() => {
    const container = qaResultRef.current;
    if (!container) return;

    const handleScroll = () => {
      // 忽略自动滚动触发的scroll事件
      const currentPosition = container.scrollTop + container.clientHeight;
      const { scrollHeight } = container;

      // 计算当前滚动位置与底部的距离
      const distanceToBottom = scrollHeight - currentPosition;

      // 如果用户主动滚动离开底部区域（距离大于20px），禁用自动滚动
      if (distanceToBottom > 20 && autoScrollEnabled.current) {
        autoScrollEnabled.current = false;
        // 清除已有定时器
        if (userScrollTimeout.current) {
          clearTimeout(userScrollTimeout.current);
        }
      } else if (distanceToBottom <= 20 && !autoScrollEnabled.current) {
        // 如果用户滚动回到底部区域，重新启用自动滚动
        autoScrollEnabled.current = true;
      }

      // 5秒后恢复自动滚动（仅在回答中时）
      if (!autoScrollEnabled.current && (isAnswering || connectLoading)) {
        if (userScrollTimeout.current) {
          clearTimeout(userScrollTimeout.current);
        }
        userScrollTimeout.current = setTimeout(() => {
          autoScrollEnabled.current = true;
          smartScroll();
        }, 5000);
      }

      lastScrollPosition.current = currentPosition;
    };

    // 启用滚动事件监听器
    container.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => {
      // 移除滚动事件监听器
      container.removeEventListener('scroll', handleScroll);
      if (userScrollTimeout.current) {
        clearTimeout(userScrollTimeout.current);
      }
    };
  }, [isAnswering, connectLoading]);

  return (
    <div style={style} className={styles.qaContentWrapper}>
      <div className={styles.treatmentHeader}>
        <div className={styles.treatmentTitle}>基于NCCN指南的病例智能问答</div>
        <div className={styles.treatmentActions}>
          <MenuUnfoldOutlined
            style={{ fontSize: '16px', cursor: 'pointer', color: '#0b214573' }}
            onClick={onClose}
          />
        </div>
      </div>

      {(analyzing || loadingHistory) && !historyData && (
        <div className={styles.loadingContainer}>
          <LoadingIcon
            animation={false}
            description={!loadingHistory ? '正在分析病例数据...' : '正在获取历史记录...'}
          />
        </div>
      )}

      {nccnConfirmVisible && !analyzing && (
        <div className={styles.nccnConfirmContainer}>
          <div className={styles.nccnIconContainer}>
            <ReadOutlined style={{ fontSize: 60 }} />
          </div>
          <h3 className={styles.nccnConfirmTitle}>
            基于上传的病例信息，系统已匹配相关的NCCN指南，请您确认后，进行下一步
          </h3>
          <div className={styles.nccnSelectContainer}>
            <Select
              style={{ width: '300px', textAlign: 'left' }}
              value={selectedNccnType}
              onChange={handleNccnChange}
              options={guidelineData?.map((item: any) => ({
                value: item,
                label: item,
              }))}
              placeholder="请选择NCCN指南"
              allowClear
            />
          </div>
          <div className={styles.nccnConfirmActions}>
            <Button disabled={!selectedNccnType} type="primary" onClick={confirmQaNccnGuide}>
              下一步
            </Button>
          </div>
        </div>
      )}

      {!!sessionInsightId && (
        <div className={styles.qaMainContent}>
          <Spin
            spinning={loadingQuestion}
            style={{ display: 'flex' }}
            indicator={
              <LoadingIcon
                animation={false}
                description="数据加载中..."
                descStyle={{ color: '#0365bf' }}
              />
            }
          >
            {/* 标签导航 */}
            <div className={styles.qaTabs}>
              <div className={classNames(styles.qaTab)}>
                推荐问题：
                <span
                  className={styles.refreshButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    httpGetMedicalInsightQuestionReq(sessionInsightId);
                  }}
                >
                  <SyncOutlined />
                  刷新
                </span>
              </div>
              <div className={styles.qaTabActions} onClick={toggleContentExpand}>
                {isContentExpanded ? '收起' : '展开'}{' '}
                {isContentExpanded ? (
                  <CaretUpOutlined style={{ fontSize: '12px' }} />
                ) : (
                  <CaretDownOutlined style={{ fontSize: '12px' }} />
                )}
              </div>
            </div>

            {/* 内容区域 */}
            {questionData && (
              <div
                className={styles.qaTabContent}
                style={{
                  maxHeight: isContentExpanded ? '200px' : '0',
                  opacity: isContentExpanded ? 1 : 0,
                  padding: isContentExpanded ? '0 16px 16px 16px' : '0 16px',
                  overflow: isContentExpanded ? 'auto' : 'hidden',
                }}
              >
                {questionData?.diseaseSpecificQuestions?.map((question: any) => (
                  <div
                    key={question}
                    className={classNames(styles.crsItem)}
                    onClick={() => handleQuestionClick(question)}
                  >
                    {question}
                  </div>
                ))}
                <div className={styles.recommendedQuestions}>
                  {questionData?.questions?.map((question: any) => (
                    <div
                      key={question}
                      className={styles.questionItem}
                      onClick={() => handleQuestionClick(question)}
                    >
                      {question}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!questionData && <Empty description={loadingQuestion ? '' : '暂无推荐问题'} />}
          </Spin>
          {isContentExpanded && <div style={{ height: 16 }} />}
          {/* 问答结果显示区域 */}
          {!!sessionInsightId && (
            <>
              <div className={styles.qaResultArea} ref={qaResultRef}>
                {historyData?.qaHistory?.map((item: any, index: number) => (
                  <QAResultItem
                    key={item?.createTime}
                    question={item?.question}
                    guidelineName={item?.guidelineName}
                    thinkingContent={
                      item?.answer?.indexOf('</think>') !== -1
                        ? item?.answer
                            ?.split('</think>')[0]
                            ?.replace('<think>', '')
                            ?.replace('[br]', '')
                        : ''
                    }
                    answer={
                      item?.answer?.indexOf('</think>') !== -1
                        ? item?.answer?.split('</think>')[1]
                        : item?.answer
                    }
                  />
                ))}
                {(thinkingContent || answerContent) && isAnswering && (
                  <QAResultItem
                    collapsed={false}
                    thinking={thinking}
                    question={currentQuestion || qaParams?.question}
                    answering={isAnswering}
                    thinkingContent={thinkingContent}
                    answer={answerContent}
                  />
                )}
                {!historyData?.qaHistory?.length && !isAnswering && (
                  <Empty
                    image={emptyMessageIcon}
                    style={{ paddingTop: 50 }}
                    description={
                      <>
                        您可以提问关于此病例的检查及诊疗相关问题
                        <br />
                        AI将根据NCCN指南为您解答
                      </>
                    }
                  />
                )}
                {connectLoading && !isReconnecting && (
                  <LoadingIcon animation description="正在分析病例数据..." />
                )}
                {isReconnecting && <LoadingIcon animation description="正在恢复会话..." />}
              </div>
              {/* 输入框区域 */}
              <div className={styles.qaInputArea}>
                {/* <span className={styles.qaAnswerInfo}>
                  温馨提示：本问答助手仅支持单轮对话，不支持追问功能。若您有进一步的问题，请重新发起提问，我们会根据病例和指南为您提供帮助。
                </span> */}
                <div className={styles.qaInputContainer}>
                  <Input
                    className={styles.qaInput}
                    placeholder={getInputPlaceholder()}
                    value={inputQuestion}
                    disabled={isAnswering || !canAsk}
                    onChange={(e) => setInputQuestion(e.target.value)}
                    onPressEnter={() => {
                      if (isAnswering) return;
                      handleSubmitQuestion();
                    }}
                  />
                  <Button
                    loading={isAnswering}
                    type="primary"
                    disabled={!canAsk || isAnswering}
                    size="large"
                    onClick={handleSubmitQuestion}
                  >
                    {getButtonText()}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default IntelligentQA;
